import React, { useCallback } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  Badge,
  Button,
  CloseButton,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  InputGroup,
  SimpleGrid,
  Text,
} from '@chakra-ui/react'

//UI
import { Input, VersionFileInput } from '@/components/Form'

//State

//Helper

import { formatOffer } from '@/features/offers/helpers/formatOffer'

//Constant
import { CostBudgetType, TenderDocumentStatus, SourcingPostType } from '@eapproval/offer-shared'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { OfferDocumentTypes, OfferStage, RoleInOffer } from '@/features/offers/constants'
import { getStage } from '@/features/offers/helpers'
import useOffer from '@/features/offers/hooks/useOffer'
import {
  getTenderDocumentLabel,
  prepareAndValidateSourcingPostData,
} from '@/features/offers/offer-detail/helper/sourcing.helper'
import { selectOfferDetail } from '@/features/offers/slices/offerDetail.slice'
import { useConfirmationModalManagement } from '@/stores/ui/confirmationModalSlice'
import { showModal } from '@/stores/ui/modalSlice'
import { hideLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { MODAL_TYPES } from '@/utils/constants/modalTypes'
import CostBudgetQuotationTemplateDetailSection from './CostBudgetQuotationTemplateDetailSection'
import { useCostBudgetAccess } from '../CostBudgetAccessProvider'

const renderStatusBadge = (status) => {
  let badgeColor = 'gray.100'
  switch (status) {
    case 'draft':
      badgeColor = 'gray.100'
      break
    case 'submitted':
      badgeColor = 'yellow'
      break
    case 'posted':
      badgeColor = 'green'
      break
  }

  return <Badge bg={badgeColor}>{status}</Badge>
}

const CostBudgetTenderDocumentCard = ({
  isOpen = false,
  costBudgetIndex,
  tenderDocumentIndex,
  removeTenderDocument,
}) => {
  const { role } = useSelector(selectOfferDetail)
  const { canEditTenderDocuments, canEditVendor } = useCostBudgetAccess()
  const { open: openConfirmationModal } = useConfirmationModalManagement()

  const { getValues } = useFormContext()
  const { useSubmitTenderDocumentToProcurement, useInactiveTenderDocument, useActivateTenderDocument } =
    useOfferCostBudget()
  const { useUpdateOffer } = useOffer()

  const updateOffer = useUpdateOffer()

  const submitTenderDocumentToProcurement = useSubmitTenderDocumentToProcurement()
  const inactiveTenderDocument = useInactiveTenderDocument()
  const activateTenderDocument = useActivateTenderDocument()
  const dispatch = useDispatch()
  const {
    fields: otherAnnexs,
    append: addAnnex,
    remove: removeAnnex,
  } = useFieldArray({
    name: `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.otherAnnexs`,
  })

  const tenderDocument = useWatch({
    name: `sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}`,
  })
  const requireProcurementOfficer =
    useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.requireProcurementOfficer` }) ?? false
  const costBudgetType = useWatch({ name: `sourcing.costBudgets.${costBudgetIndex}.detail.type` })

  const isPosted = tenderDocument.status === TenderDocumentStatus.POSTED

  const isTenderDocumentActive = tenderDocument.status !== TenderDocumentStatus.INACTIVE

  // Editability follows the cost budget access context, so the card turns read-only whenever the cost
  // budget does (offer locked, inactive cost budget, or the [All] sub-system filter)
  const canEditTenderDocument = isTenderDocumentActive && canEditTenderDocuments

  const isEmptyTenderDocument =
    tenderDocument.status === TenderDocumentStatus.DRAFT &&
    tenderDocument.annex1.length === 0 &&
    tenderDocument.annex2.length === 0 &&
    tenderDocument.annex3.length === 0 &&
    tenderDocument.otherAnnexs.length === 1 &&
    tenderDocument.otherAnnexs[0].name === '' &&
    tenderDocument.otherAnnexs[0].files.length === 0 &&
    (!tenderDocument.remark || tenderDocument.remark === '')

  const handleSubmitTenderDocumentToProcurement = async () => {
    //Update Tender Doc. Status
    // Add History to workflow

    try {
      if (
        tenderDocument.annex1.length === 0 &&
        tenderDocument.annex2.length === 0 &&
        tenderDocument.annex3.length === 0 &&
        tenderDocument.otherAnnexs.length === 0 &&
        tenderDocument?.remark === ''
      ) {
        throw new Error('Please add some content before submit to Procurement Officer')
      }
      //Save Offer First
      const currentOffer = getValues()
      const formattedOffer = formatOffer(currentOffer)

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })

      const offerId = getValues('_id')
      const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)

      if (!offerId || offerId === '') {
        throw new Error('Missing Offer Id')
      }

      if (!costBudgetId || costBudgetId === '') {
        throw new Error('Missing CostBudget Id')
      }
      await submitTenderDocumentToProcurement.mutateAsync({
        costBudgetId: costBudgetId,
        tenderDocumentIndex: tenderDocumentIndex,
        offerId: offerId,
      })
    } catch (err) {
      toast.error(err?.message ?? '')
    }
  }
  const handleInactiveTenderDocument = async () => {
    try {
      //Save Offer First
      if (isEmptyTenderDocument) {
        removeTenderDocument(tenderDocumentIndex)
        return
      }

      const { isConfirmed } = await openConfirmationModal({
        title: 'Confirm to inactive / remove tender document',
        message: 'Filled Information will be lost',
      })
      if (!isConfirmed) {
        //Stop current action, and don't open approval modal
        return
      }

      const currentOffer = getValues()
      const formattedOffer = formatOffer(currentOffer)

      await updateOffer.mutateAsync({
        id: currentOffer._id,
        data: formattedOffer,
      })
      const offerId = getValues('_id')
      const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)

      if (!offerId || offerId === '') {
        throw new Error('Missing Offer Id')
      }

      if (!costBudgetId || costBudgetId === '') {
        throw new Error('Missing CostBudget Id')
      }
      await inactiveTenderDocument.mutateAsync({
        costBudgetId: costBudgetId,
        tenderDocumentIndex: tenderDocumentIndex,
      })
    } catch (err) {
      toast.error(err?.message ?? '')
    }
  }

  const handlePostRFQ = useCallback(async () => {
    try {
      const offer = getValues()
      const formattedOffer = formatOffer(offer)

      const offerId = getValues(`_id`)

      const costBudgetDetail = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail`)

      const { vendorDetails, technicalUsers, ccList, procurementOfficer } = prepareAndValidateSourcingPostData({
        offer,
        costBudgetDetail,
        costBudgetIndex,
        tenderDocumentIndex,
        requireTenderDocumentContent: true,
      })

      await updateOffer.mutateAsync({
        id: offerId,
        data: formattedOffer,
      })

      dispatch(
        showModal({
          modalType: MODAL_TYPES.RFQ,
          modalProps: {
            title:
              tenderDocumentIndex === 0
                ? 'Post New Quotation Request'
                : `Post ${getTenderDocumentLabel(tenderDocumentIndex)}`,
            //Send RFQ
            offerId: offerId,
            costBudgetId: costBudgetDetail._id,
            tenderDocumentIndex: tenderDocumentIndex,
            vendorDetails: vendorDetails,
            procurementOfficer: procurementOfficer,
            technicalUsers: technicalUsers,
            ccList: ccList,
            requestType: SourcingPostType.REQUEST_FOR_QUOTATION,

            canChangePostType: false,
          },
        })
      )
    } catch (res) {
      toast.error(
        res?.message?.endsWith('No Active Tender Document / Addendum Found')
          ? `[Unexpected Error] ${res.message}`
          : res?.message
      )
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }, [costBudgetIndex, tenderDocumentIndex, dispatch, getValues])

  const handleRemoveAnnex = async (annexIndex) => {
    const { isConfirmed } = await openConfirmationModal({
      title: 'Confirm',
      message: 'Are you sure you want to remove this annex?',
    })

    if (!isConfirmed) {
      return
    }
    removeAnnex(annexIndex)
  }

  const handleActivateTenderDocument = async () => {
    try {
      const costBudgetId = getValues(`sourcing.costBudgets.${costBudgetIndex}.detail._id`)
      if (!costBudgetId || costBudgetId === '') {
        throw new Error('Missing CostBudget Id')
      }
      await activateTenderDocument.mutateAsync({
        costBudgetId: costBudgetId,
        tenderDocumentIndex: tenderDocumentIndex,
      })
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <>
      <Flex gap="2" justifyContent={'flex-end'}>
        {tenderDocument.status === TenderDocumentStatus.DRAFT &&
          canEditTenderDocument &&
          requireProcurementOfficer &&
          role !== RoleInOffer.PROCUREMENT_OFFICER && (
            <Button colorScheme={'blue'} onClick={() => handleSubmitTenderDocumentToProcurement(tenderDocumentIndex)}>
              Submit to Procurement
            </Button>
          )}

        {[TenderDocumentStatus.DRAFT, TenderDocumentStatus.SUBMITTED].includes(tenderDocument.status) &&
          tenderDocumentIndex !== 0 &&
          canEditTenderDocument && (
            <Button colorScheme={'red'} onClick={() => handleInactiveTenderDocument(tenderDocumentIndex)}>
              {isEmptyTenderDocument ? 'Remove' : 'Inactive'}
            </Button>
          )}

        {canEditTenderDocument && !isPosted && (
          <Button
            colorScheme={'green'}
            onClick={() => {
              addAnnex({ name: '', files: [] })
            }}
          >
            Add Annex File
          </Button>
        )}

        {[TenderDocumentStatus.DRAFT, TenderDocumentStatus.SUBMITTED].includes(tenderDocument.status) &&
          canEditVendor && (
            <Button colorScheme={'teal'} onClick={handlePostRFQ}>
              Post RFQ to Vendor(s)
            </Button>
          )}
        {tenderDocument.status === TenderDocumentStatus.INACTIVE && canEditTenderDocuments && (
          <Button colorScheme={'blue'} onClick={handleActivateTenderDocument}>
            Activate
          </Button>
        )}
      </Flex>
      <Flex direction={'column'} my="2" gap="3">
        {tenderDocumentIndex === 0 && (
          <SimpleGrid columns={[2, null, 2]} spacingX="20px" spacingY="10px">
            <VersionFileInput
              name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.annex1`}
              label={'Annex 1: Particular Specifications'}
              fileType={OfferDocumentTypes.SOURCING_ANNEX}
              canUpload={canEditTenderDocument && !isPosted}
              isMultiple
              showVersion={false}
              isRequired={false}
            />
            <VersionFileInput
              name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.annex2`}
              label={'Annex 2: Schedule of Quantity'}
              fileType={OfferDocumentTypes.SOURCING_ANNEX}
              canUpload={canEditTenderDocument && !isPosted}
              isMultiple
              showVersion={false}
              isRequired={false}
            />
            <VersionFileInput
              name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.annex3`}
              label={'Annex 3: Drawings'}
              fileType={OfferDocumentTypes.SOURCING_ANNEX}
              canUpload={canEditTenderDocument && !isPosted}
              isMultiple
              showVersion={false}
              isRequired={false}
            />
            <Text color={'black'} fontWeight={'semibold'} fontSize="14px">
              Annex 4: Conditions of Purchase
            </Text>
          </SimpleGrid>
        )}
        {Array.isArray(otherAnnexs) &&
          otherAnnexs.map((other, annexIndex) => (
            <FormControl as={Flex} key={other.id}>
              <HStack w="43%" mr="12px">
                <FormLabel>Annex</FormLabel>
                <Input
                  name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.otherAnnexs.${annexIndex}.name`}
                  type="text"
                  disabled={!canEditTenderDocument || isPosted}
                />
                {canEditTenderDocument && !isPosted && <CloseButton onClick={() => handleRemoveAnnex(annexIndex)} />}
              </HStack>
              <InputGroup>
                <VersionFileInput
                  name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.otherAnnexs.${annexIndex}.files`}
                  fileType={OfferDocumentTypes.SOURCING_ANNEX}
                  canUpload={canEditTenderDocument && !isPosted}
                  isRow
                  labelWidth={40}
                  isMultiple
                />
              </InputGroup>
            </FormControl>
          ))}
        <Input
          name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.remark`}
          label={'Remark to vendor'}
          disabled={!canEditTenderDocument || isPosted}
          isRequired={false}
          isRow
          labelWidth={'20'}
        />

        {costBudgetType === CostBudgetType.FRAME_AGREEMENT && (
          <Flex direction={'column'} gap="1" px="2">
            <Text as="b">Frame Agreement</Text>
            <VersionFileInput
              name={`sourcing.costBudgets.${costBudgetIndex}.detail.tenderDocuments.${tenderDocumentIndex}.frameAgreements`}
              label="Frame Agreement"
              fileType={OfferDocumentTypes.SOURCING_FRAME_AGREEMENT}
              canUpload={canEditTenderDocument && !isPosted}
              isMultiple
              showVersion={false}
              isRequired={false}
            />
          </Flex>
        )}

        {costBudgetType !== CostBudgetType.FRAME_AGREEMENT && (
          <CostBudgetQuotationTemplateDetailSection
            costBudgetIndex={costBudgetIndex}
            tenderDocumentIndex={tenderDocumentIndex}
            disabled={!canEditTenderDocument || isPosted}
          />
        )}
      </Flex>
    </>
  )
}

export default CostBudgetTenderDocumentCard
